/** @jsxImportSource @emotion/react */
import React, { Suspense, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import { Loading, Flex, Text } from "@phoenix/components";
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from "recharts";
import { QualitySummaryTable, getScorerColor, squareIconCSS } from "./QualitySummaryTable";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { CheckmarkCircle } from "@phoenix/components/core/icon/Icons";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const cardCSS = css`
  background: var(--ac-global-background-color-white);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
`;

const cardHeaderCSS = css`
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 12px;
`;

const cardTitleCSS = css`
  font-size: 18px;
  font-weight: 700;
  color: var(--ac-global-text-color-900);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const avgScoreCSS = (color: string) => css`
  margin-top: 4px;
  font-size: 24px;
  font-weight: 800;
  color: ${color};
`;

const chartGridCSS = css`
  display: block;
  min-height: 200px;
`;

const chartSectionCSS = css`
  display: flex;
  flex-direction: column;
`;

const chartTitleCSS = css`
  font-size: 13px;
  font-weight: 600;
  color: var(--ac-global-text-color-500);
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DESIRED_NAMES = ["Hallucination", "QA Correctness", "Human_vs_AI", "Toxicity", "Tool_Calling"];

function QualityDetailCard({
    projectId,
    timeRange,
    name,
    timeSeriesData,
    fetchKey
}: {
    projectId: string;
    timeRange: any;
    name: string;
    timeSeriesData: any[];
    fetchKey?: number;
}) {
    const data = useLazyLoadQuery<any>(
        graphql`
            query QualityDashboardDetailQuery($id: ID!, $name: String!, $timeRange: TimeRange!) {
                project: node(id: $id) {
                    ... on Project {
                        spanAnnotationSummary(annotationName: $name, timeRange: $timeRange) {
                            meanScore
                            labelFractions {
                                label
                                fraction
                            }
                        }
                    }
                }
            }
        `,
        {
            id: projectId,
            name,
            timeRange: {
                start: timeRange.start?.toISOString(),
                end: timeRange.end?.toISOString(),
            },
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const summary = data.project?.spanAnnotationSummary;
    if (!summary) return null;

    const scorerColor = getScorerColor(name);
    const score = summary.meanScore;
    
    const labelData = (summary.labelFractions ?? []).map((lf: any) => ({
        labelKey: lf.label,
        val: Math.max(0.5, lf.fraction * 100), // Ensure at least a tiny sliver is visible if value is > 0
    }));

    return (
        <div css={cardCSS}>
            <div css={cardHeaderCSS} style={{ marginBottom: '16px' }}>
                <div css={squareIconCSS(name)} style={{ marginTop: '6px' }} />
                <div>
                    <div css={cardTitleCSS} style={{ textTransform: 'lowercase' }}>{name.replace(/_/g, " ")}</div>
                    <div css={avgScoreCSS(scorerColor)} style={{ marginTop: '0px' }}>
                        {score != null ? `${score.toFixed(2)}%` : "--"} <span style={{fontSize: '12px', fontWeight: 500, color: 'var(--ac-global-text-color-400)'}}>avg score</span>
                    </div>
                </div>
            </div>

            <div css={chartGridCSS}>
                <div css={chartSectionCSS}>
                    <div css={chartTitleCSS} style={{ 
                        textTransform: 'none', 
                        color: 'var(--ac-global-text-color-700)', 
                        marginBottom: '20px', 
                        fontSize: '14px', 
                        fontWeight: 600,
                        borderLeft: `3px solid ${scorerColor}`,
                        paddingLeft: '12px'
                    }}>
                        Total aggregate scores
                    </div>
                    <ResponsiveContainer width="100%" height={Math.max(140, labelData.length * 50 + 40)}>
                        <BarChart data={labelData} layout="vertical" margin={{ left: 120, right: 60, top: 0, bottom: 0 }}>
                            <CartesianGrid horizontal={false} vertical stroke="var(--ac-global-color-grey-100)" strokeDasharray="0" />
                            <XAxis 
                                type="number" 
                                domain={[0, 100]}
                                hide
                            />
                            <YAxis 
                                dataKey="labelKey" 
                                type="category" 
                                stroke="var(--ac-global-text-color-700)" 
                                fontSize={14} 
                                width={120}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'var(--ac-global-text-color-800)', fontWeight: 500 }}
                            />
                            <Tooltip 
                                formatter={(val: number) => `${val.toFixed(2)}%`}
                                cursor={{ fill: 'rgba(0,0,0,0.02)' }}
                                contentStyle={{ borderRadius: '8px', border: '1px solid var(--ac-global-color-grey-200)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: '8px' }}
                            />
                            <Bar 
                                dataKey="val" 
                                fill={scorerColor} 
                                radius={[0, 4, 4, 0]} 
                                barSize={32}
                                isAnimationActive={false}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                    <Flex justifyContent="center" marginTop="12px">
                        <Flex alignItems="center" gap="8px">
                            <div style={{ width: '12px', height: '12px', background: scorerColor, borderRadius: '2px' }} />
                            <Text size="S" weight="heavy" color="text-700">count</Text>
                        </Flex>
                    </Flex>
                </div>
            </div>
        </div>
    );
}

const toolStatGridCSS = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 8px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const toolStatCardCSS = css`
  background: var(--ac-global-background-color-white);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
    border-color: var(--ac-global-color-primary-30);
  }
`;

const toolIconWrapperCSS = (color: string) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${color}15;
  color: ${color};
  font-size: 20px;
`;

export function QualityDashboard({
    projectId,
    timeRange,
    fetchKey,
    onDataExport,
}: ProjectMetricViewProps) {
    const data = useLazyLoadQuery<any>(
        graphql`
            query QualityDashboardQuery($id: ID!, $timeRange: TimeRange!) {
                project: node(id: $id) {
                    ... on Project {
                        spanAnnotationAgentSummaries(timeRange: $timeRange) {
                            name
                            meanScore
                            annotatorKind
                            numScores
                        }
                        spanAnnotationScoreTimeSeries(timeRange: $timeRange) {
                            data {
                                timestamp
                                scoresWithLabels {
                                    label
                                    score
                                }
                            }
                        }
                    }
                }
            }
        `,
        {
            id: projectId,
            timeRange: {
                start: timeRange.start?.toISOString(),
                end: timeRange.end?.toISOString(),
            },
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const timeSeriesData = data.project?.spanAnnotationScoreTimeSeries?.data ?? [];
    const summaries = data.project?.spanAnnotationAgentSummaries ?? [];

    const totalScores = summaries.reduce((acc: number, s: any) => acc + s.numScores, 0);
    const avgQuality = summaries.length > 0 ? (summaries.reduce((acc: number, s: any) => acc + s.meanScore, 0) / summaries.length) : 0;
    const activeScorers = new Set(summaries.map((s: any) => s.name)).size;
    const autoScores = summaries.filter((s: any) => s.annotatorKind === 'CODE' || s.annotatorKind === 'LLM').reduce((acc: number, s: any) => acc + s.numScores, 0);
    const autoRate = totalScores > 0 ? (autoScores / totalScores) * 100 : 100;

    return (
        <div css={containerCSS}>
            {/* 1. Stat Cards (Matching Tool Calls style) */}
            <div css={toolStatGridCSS}>
                <div css={toolStatCardCSS}>
                    <div css={toolIconWrapperCSS("#4f46e5")}>🎯</div>
                    <div>
                        <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>
                            {avgQuality.toFixed(2)}%
                        </div>
                        <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>
                            Avg Quality
                        </div>
                    </div>
                </div>

                <div css={toolStatCardCSS}>
                    <div css={toolIconWrapperCSS("#3b82f6")}>📊</div>
                    <div>
                        <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>
                            {totalScores.toLocaleString()}
                        </div>
                        <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>
                            Total Evaluations
                        </div>
                    </div>
                </div>

                <div css={toolStatCardCSS}>
                    <div css={toolIconWrapperCSS("#10b981")}>🧩</div>
                    <div>
                        <div css={css`font-size: 20px; font-weight: 700; color: var(--ac-global-text-color-900); text-align: left;`}>
                            {activeScorers}
                        </div>
                        <div css={css`font-size: 11px; color: var(--ac-global-text-color-500); font-weight: 500;`}>
                            Active Scorers
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Summary Table */}
            <QualitySummaryTable 
                projectId={projectId} 
                timeRange={timeRange} 
                fetchKey={fetchKey} 
                onDataExport={onDataExport} 
            />

            {/* 3. Detailed Cards */}
            <Flex direction="column" gap="24px">
                {DESIRED_NAMES.map(name => (
                    <Suspense key={name} fallback={<Loading />}>
                        <QualityDetailCard 
                            projectId={projectId} 
                            timeRange={timeRange} 
                            name={name} 
                            timeSeriesData={timeSeriesData}
                            fetchKey={fetchKey}
                        />
                    </Suspense>
                ))}
            </Flex>
        </div>
    );
}